import React, { useContext, useEffect, useState, useRef } from 'react';
import {
  Divider, Grid, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField
} from '@mui/material';
import { EditOutlined } from '@mui/icons-material';
import { connect, useDispatch } from 'react-redux';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { setRoomAlert } from '../../redux/room/room.actions';
import http from '../../services/http';
import { marcaDaSala } from '../../utils/marcaDaSala';
import { telefoneDaSala, telefoneLegivel } from '../../utils/telefoneDaSala';
import { toast } from 'react-toastify';
import FaseDoRelacionamento, { useFasesDoRelacionamento } from '../FaseDoRelacionamento';
import ObservacaoDoCliente from '../ObservacaoDoCliente';
import './client-information.css';

const ClientInformation = ({ client, room, user, permissions }) => {
  const [currentClient, setCurrentClient] = useState({});
  const fasesDoRelacionamento = useFasesDoRelacionamento();
  const { theme } = useContext(ThemeContext);
  const dispatch = useDispatch();
  const lastRoomIdRef = useRef(null);
  /**
   * Número comprido demais para ser telefone: é um grupo de WhatsApp.
   *
   * A conversão para texto não é defensividade solta -- ela conserta uma tela
   * QUEBRADA. Era `number.length > 15`, e o ramo que chama isto recebe o
   * cliente SINTÉTICO que o `ChatOptions` monta quando não há cadastro:
   * `{ name: room.name, email: room.name, telefone: room.name }`. E `rooms`
   * NÃO TEM coluna `name`, então os três campos chegam `undefined` e a ficha
   * derrubava a tela inteira com "Cannot read properties of undefined
   * (reading 'length')".
   *
   * O buraco e o cliente sintético são anteriores a 12/10/2026; o que mudou
   * naquele dia foi a ORDEM em `RoomList.handleSelectRoom` -- o `await` que
   * ficava antes de trocar de sala garantia, por acidente, que `currentClient`
   * já estivesse preenchido quando este componente montava. Com a troca de
   * sala imediata (que é o ganho de desempenho), o ramo sintético passou a
   * ser alcançado de verdade.
   */
  const isGroup = (number) => String(number ?? '').length > 15;

  const canEditClient = Array.isArray(permissions) && permissions.includes('#editar-dados-cliente');
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editCode, setEditCode] = useState('');
  const [savingName, setSavingName] = useState(false);
  // Empresa exibida (sobrescrita após uma re-associação por registro).
  const [companyOverride, setCompanyOverride] = useState(null);

  const currentCompanyCode = companyOverride?.code ?? client?.company?.code ?? '';

  const openEditName = () => {
    setEditName(currentClient.name || '');
    setEditCode(String(currentCompanyCode || ''));
    setEditOpen(true);
  };

  const saveClientName = async () => {
    const clientId = client?.id || currentClient?.id;
    if (!clientId) {
      toast.error('Não foi possível identificar o cliente.');
      return;
    }
    const name = (editName || '').trim();
    if (!name) {
      toast.warning('Informe um nome.');
      return;
    }
    const code = String(editName != null ? editCode : '').trim();
    const codeChanged = code !== '' && String(code) !== String(currentCompanyCode || '');
    try {
      setSavingName(true);
      if (codeChanged) {
        // Re-associa o cliente à empresa do novo registro (valida no webservice).
        const res = await http.put(`/clients/${clientId}/registration`, { code, name });
        if (res.data?.company) {
          setCompanyOverride(res.data.company);
        }
        setCurrentClient((prev) => ({ ...prev, name, company: res.data?.company || prev.company, company_id: res.data?.company_id }));
        toast.success('Registro atualizado e cliente re-associado à empresa.');
      } else {
        await http.put(`/clients/${clientId}`, { name });
        setCurrentClient((prev) => ({ ...prev, name }));
        toast.success('Nome do cliente atualizado.');
      }
      setEditOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao atualizar os dados do cliente.');
    } finally {
      setSavingName(false);
    }
  };

  // Nome do cliente com botão de edição (quando o operador tem permissão).
  const ClientNameDisplay = ({ className, sx }) => (
    <Grid item className={className} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...sx }}>
      <span>{currentClient.name}</span>
      {canEditClient && (
        <Tooltip title="Editar dados do cliente" arrow>
          <IconButton size="small" onClick={openEditName} sx={{ p: 0.25 }}>
            <EditOutlined sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Grid>
  );

  // `formatPhoneNumber` saiu daqui: virou `telefoneLegivel`, em
  // `utils/telefoneDaSala`, porque agora os três ramos da ficha mostram o
  // número e a regra não pode morar dentro de um só componente. A versão de
  // lá ganhou o ramo do fixo de oito dígitos, que este não tinha -- um fixo
  // saía cru no meio de números formatados.

  const formatDate = (dateString) => {
    if (!dateString) return dateString;
    
    try {
      const date = new Date(dateString);
      
      if (isNaN(date.getTime())) {
        return dateString; // Retorna a string original se não conseguir converter
      }
      
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return dateString; // Retorna a string original em caso de erro
    }
  };

  useEffect(() => {
    if (!room) return;
    
    // Evita processar a mesma room múltiplas vezes (previne loops infinitos)
    const roomId = room.room_id || (room.whatsapp ? room.contact?.id?.user : null);
    if (roomId === lastRoomIdRef.current) {
      return; // Já processou esta room, não precisa processar novamente
    }
    
    lastRoomIdRef.current = roomId;
    
    if (room.client) {
      setCurrentClient(room.client);
      // invited_users pode vir null, string JSON ou já como array. Normaliza para
      // um array sempre — antes JSON.parse(null) virava null e quebrava a tela no
      // .includes() abaixo (tela branca).
      let invitedUsers = [];
      try {
        const parsed = Array.isArray(room.invited_users)
          ? room.invited_users
          : JSON.parse(room.invited_users || '[]');
        invitedUsers = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        invitedUsers = [];
      }
      const isUserAuthorized = room.user_id === user.id || invitedUsers.includes(user.id);
      dispatch(setRoomAlert(false));
      if (!isUserAuthorized) {
        console.log("Você não está autorizado a ver esta sala.");
        dispatch(setRoomAlert(true));
      }
    } else if (room.whatsapp) {
      setCurrentClient({ name: room.name });
    } else {
      console.error('room ou room.client é undefined');
    }
  }, [room?.room_id, room?.whatsapp, dispatch, user?.id]);

  // Empresa exibida: usa a re-associada (companyOverride) quando houver, senão a do cliente.
  const displayCompany = companyOverride || client.company || {};

  // Por qual marca o cliente chegou. SysFar e MAXSMARTPDV sao duas marcas no
  // MESMO tenant, cada uma com o seu numero -- sem isto o operador se
  // apresenta pela marca errada.
  //
  // A guarda de canal (so ha marca em sala de WhatsApp) mora em marcaDaSala,
  // e nao aqui, para as telas nao a derivarem cada uma por conta.
  //
  // Um unico bloco, usado nos TRES ramos abaixo: contato com empresa, contato
  // com `company_id` nulo e contato sem a propriedade `company_id`. Os dois
  // ultimos sao o do visitante -- quem chegou por canal de porta hibrida sem
  // informar registro --, que e justamente quem mais precisa da marca. Um
  // bloco por ramo deixaria um deles sem.
  //
  // A cor vem do tema, e nao da classe `client-info-*`: ela fixa #FFF, que
  // some no cabecalho claro (#eef1f5). Tamanho e peso seguem os vizinhos.
  const marca = marcaDaSala(room);
  const corDoTema = { '&&': { color: theme.palette.text.primary } };
  const blocoMarca = marca ? (
    <Grid item className="info" data-testid="marca-da-conversa">
      <Grid className="client-info-header" sx={corDoTema}>Atendimento por:</Grid>
      <Grid className="client-info-content" sx={corDoTema}>{marca}</Grid>
    </Grid>
  ) : null;

  /*
   * DE QUE NÚMERO a pessoa está falando (pedido de 13/10/2026).
   *
   * ## Por que dentro de "Contato:", e não num bloco próprio
   *
   * Porque numa conversa de WhatsApp o número É o contato. Numa primeira
   * versão isto virou um bloco separado, e o resultado na tela foi um
   * "Contato:" VAZIO ao lado de um "Número de WhatsApp:" preenchido -- o
   * visitante de WhatsApp não tem e-mail, e o telefone dele estava sendo lido
   * do lugar errado (ver abaixo). O dono do produto apontou na hora:
   * "o contato ali do WhatsApp não deveria pegar o número do usuário?".
   *
   * ## O campo que a ficha lia NÃO EXISTE
   *
   * A linha do telefone era `{client.telefone}` -- e `clients` não tem coluna
   * `telefone`; tem `whatsapp_user_number`. Ou seja: aquela linha nunca
   * mostrou nada, para contato nenhum, desde sempre. O único caso em que ela
   * tinha valor era o cliente SINTÉTICO do `ChatOptions`, montado a partir de
   * `room.name` -- coluna que também não existe.
   *
   * ## Vem da SALA, com o cadastro de reserva
   *
   * A sala sabe o número que abriu ESTA conversa, que é a pergunta sendo
   * feita; o cadastro entra só quando o payload da sala não traz o telefone
   * (`/rooms/active-dashboard`). Um mesmo cadastro pode atender por mais de um
   * aparelho.
   *
   * O rótulo acompanha a linha em vez de virar cabeçalho: no ramo da empresa,
   * "Contato:" já lista o telefone DO BALCÃO, e dois números sem distinção ali
   * seriam pior que um.
   */
  const telefoneDoCliente = telefoneDaSala(room, client);
  const linhaDoWhatsApp = telefoneDoCliente ? (
    <Grid item className="client-info-content" data-testid="numero-de-quem-chamou">
      {isGroup(telefoneDoCliente) ? 'Grupo de WhatsApp' : `WhatsApp: ${telefoneLegivel(telefoneDoCliente)}`}
    </Grid>
  ) : null;

  return (
    <Grid>
      {client.hasOwnProperty("company_id") ? (
        client.company_id !== null ? (
          <Grid container flexDirection="column" className="client-information" spacing={1}>
            <Grid item xs={12} container flexDirection="column" spacing={0}>
              <ClientNameDisplay
                className="client-name"
                sx={{ marginTop: '0.5rem', marginBottom: '0.5rem', bgcolor: 'transparent' }}
              />
              <Grid item className="client-info-header" sx={{ marginTop: 0.9 }}>
                {displayCompany.name ? displayCompany.name : ""}
              </Grid>
              <Grid item className="client-info-content">
                #{displayCompany.code} {displayCompany.business_name}
              </Grid>
              {/*
                A OBSERVAÇÃO do cliente (Gisele, 19/10/2026), acima de tudo o
                resto da ficha de propósito: ela é o que muda COMO se atende,
                e enterrada entre Endereço e CEP teria o peso visual de um
                campo qualquer. Em modo `compacto` porque este painel do chat é
                estreito.

                Sem observação e sem a permissão `#observacao-cliente`, o
                componente não desenha nada -- a ficha não ganha espaço vazio.
              */}
              <Grid item sx={{ marginTop: 0.9 }}>
                <ObservacaoDoCliente empresa={displayCompany} compacto />
              </Grid>
            </Grid>
            <Divider sx={{ borderColor: theme.palette.border.secondary, marginTop: 0.9 }} variant="middle" />
            <Grid item xs={12} container spacing={1} marginLeft={0}
              sx={{ backgroundColor: theme.palette.background.default, marginBottom: '0.8rem' }}>
              <Grid item className='info'>
                <Grid item className="client-info-header">Contato:</Grid>
                <Grid item className="client-info-content">{client.email}</Grid>
                <Grid item className="client-info-content">({displayCompany.area_code}) {displayCompany.phone}</Grid>
                {linhaDoWhatsApp}
              </Grid>
              <Grid item className="info">
                <Grid className="client-info-header">Endereço:</Grid>
                <Grid className="client-info-content">
                  {`${displayCompany.address ? (`${displayCompany.state.replace(/\./g, '')} - ${displayCompany.address }`): ""}, ${displayCompany.number ? displayCompany.number : ""}`}
                </Grid>
                <Grid className="client-info-content">
                  {`${displayCompany.city ? displayCompany.city : ""}/${displayCompany.district ? displayCompany.district : ""} - ${displayCompany.zip_code}`}
                </Grid>
              </Grid>
              {blocoMarca}
              <Grid item className="info">
                <Grid className="client-info-header">Produto/Sincronização:</Grid>
                <Grid className="client-info-content">
                  {`${displayCompany.product_description ? displayCompany.product_description : ""}/${displayCompany.version_in_use ? displayCompany.version_in_use : ""}`}
                </Grid>
                <Grid item className="client-info-content">{formatDate(displayCompany.sync_date)}</Grid>
              </Grid>
              <Grid item className="info">
                <Grid className="client-info-header">Situação:</Grid>
                <Grid className="client-info-content">
                  {(() => {
                    const activeStatus = displayCompany.active;
                    switch (activeStatus) {
                      case 'S':
                        return 'Ativo';
                      case 'N':
                        return 'Bloqueado';
                      case 'C':
                        return 'Cancelado';
                      case 'T':
                        return 'Inativo';
                      default:
                        return 'Desconhecido';
                    }
                  })()}
                </Grid>
              </Grid>
              <Grid item className="info">
                <Grid className="client-info-header">Representante:</Grid>
                <Grid className="client-info-content">{displayCompany.service_group_description}</Grid>
              </Grid>
              {/*
                A fase do relacionamento (item 7). Fica ao lado de "Situação"
                de propósito, e as duas NÃO são a mesma coisa: "Situação" é
                contratual e vem do ERP (`active`, C/N/S/T), esta é digitada
                aqui dentro.

                O RÓTULO vem pronto da api (`relationship_phase_label`), e não
                de um mapa aqui: com uma cópia no front, a primeira fase nova
                divergiria entre os dois lados.

                "Não definida" em vez de vazio: as 692 empresas de produção
                começam sem fase, e um campo em branco não diz se ninguém
                preencheu ou se a tela falhou.
              */}
              <Grid item className="info">
                <Grid className="client-info-header">Fase:</Grid>
                <Grid className="client-info-content">
                  {/*
                    Mudável aqui mesmo (pedido de 05/10): a informação chega
                    DURANTE a conversa, e mandar o operador abrir o cadastro
                    para registrar é o atrito que faz o campo não ser
                    preenchido.

                    Sem a permissão `#editar-fase-relacionamento` o controle
                    desenha só o texto -- nada clicável. Um controle que
                    parece editável e responde 403 é pior que nenhum.
                  */}
                  <FaseDoRelacionamento empresa={displayCompany} fases={fasesDoRelacionamento} />
                </Grid>
              </Grid>
              <Divider sx={{ borderColor: theme.palette.border.secondary, marginTop: 0.9 }} variant="middle" />
            </Grid>
          </Grid>
        ) : (
          <Grid container flexDirection="column" className="client-information" spacing={1}>
            <Grid item xs={12} container flexDirection="column" spacing={0}>
              <ClientNameDisplay className="client-name" />
              <Grid item className="client-info-header" sx={{ marginTop: 0.9 }}>
                {client.nome}
              </Grid>
            </Grid>
            <Divider sx={{ borderColor: theme.palette.border.secondary, marginTop: 0.9 }} />
            <Grid item xs={12} container spacing={1} sx={{ marginLeft: 0 }}>
              <Grid item className="info">
                <Grid className="client-info-header">Contato:</Grid>
                <Grid className="client-info-content">{client.email || "E-mail não informado"}</Grid>
                {/*
                  * O `{client.telefone}` que ficava aqui SAIU, e não é perda:
                  * `clients` não tem coluna `telefone` -- tem
                  * `whatsapp_user_number` --, então essa linha nunca mostrou
                  * nada, para contato nenhum. Era por isso que "Contato:"
                  * aparecia vazio no visitante de WhatsApp, que é justamente
                  * quem não tem e-mail.
                  */}
                {linhaDoWhatsApp}
              </Grid>
              {blocoMarca}
            </Grid>
          </Grid>
        )
      ) : (
        <Grid container flexDirection="column" className="client-information" spacing={1}>
          <Grid item xs={12} container flexDirection="column" spacing={0}>
            <ClientNameDisplay className="client-name" />
            <Grid item className="client-info-header" sx={{ marginTop: 0.9 }}>
              Esse cliente não possui um `company_id` definido no banco de dados.
            </Grid>
          </Grid>
          <Divider sx={{ borderColor: theme.palette.border.secondary, marginTop: 0.9 }} />
          <Grid item xs={12} container spacing={1} sx={{ marginLeft: 0 }}>
            {/*
              * O rótulo e o número que ficavam aqui saíram para a
              * `linhaDoWhatsApp`, que é a mesma nos três ramos da ficha.
              *
              * Este lia `client.telefone`, e neste ramo o cliente é o
              * SINTÉTICO que o `ChatOptions` monta sem cadastro --
              * `{ name: room.name, email: room.name, telefone: room.name }` --,
              * e `rooms` não tem coluna `name`: os três campos chegavam
              * `undefined` e o rótulo aparecia com "Número não informado"
              * embaixo, em conversa de WhatsApp que TEM número.
              */}
            <Grid item className="info">
              <Grid className="client-info-header">Contato:</Grid>
              <Grid className="client-info-content">{client.email || "E-mail não informado"}</Grid>
              {linhaDoWhatsApp}
            </Grid>
            {blocoMarca}
          </Grid>
        </Grid>
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar dados do cliente</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome do cliente"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveClientName(); }}
          />
          {/*
            * Registro OU CNPJ na mesma caixa (Gisele, 19/10/2026): o cliente
            * MAX não tem registro no ERP, então perguntar só por registro
            * nunca o acharia. Quem distingue os dois formatos é a fonte do
            * tenant, no servidor -- aqui não há o que validar, e tentar
            * adivinhar o formato na tela só criaria uma segunda regra para
            * divergir da primeira.
            */}
          <TextField
            margin="dense"
            label="Registro ou CNPJ"
            fullWidth
            value={editCode}
            onChange={(e) => setEditCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveClientName(); }}
            helperText="Informe o registro do ERP ou o CNPJ (clientes MAX não têm registro). O código é validado e o cliente é re-associado à empresa correspondente."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} color="inherit" disabled={savingName}>Cancelar</Button>
          <Button onClick={saveClientName} variant="contained" disabled={savingName}>{savingName ? 'Salvando...' : 'Salvar'}</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

const mapStateToProps = ({ companies, clients, user }) => ({
  company: companies,
  clients: clients,
  permissions: user.permissions,
});

export default connect(mapStateToProps, null)(ClientInformation);
